import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { TitleService } from './../services/title.service';
import { LocalStorageService } from 'ngx-webstorage';
import { ApiService } from './../services/api.service';
import { UserService } from './../services/user.service';
import { MobileApp } from '../classes/mobile-app';
import {
  EditProfileModel,
  ChangePasswordModel,
} from './models';
import { EditProfileService } from '../services/edit-profile.service';
import { MatMenuTrigger } from '@angular/material/menu';
import { SocketIoService } from '../services/socket-io.service';
import { environment } from '../../environments/environment';
import { BillingService } from '../services/billing.service';
import { ISubscriptionPlan } from '../billing/types-and-const/billing-types';
import { ChatService, VideoCallStatus } from '../chat/services/chat.service';
import { EmergencyContact } from '../utils/types';
import { MatDialog } from '@angular/material/dialog';
import { AddEmergencyContactDialogComponent } from '../dialogs/add-emergency-contact-dialog/add-emergency-contact-dialog.component';
import { DeleteEmergencyContactDialogComponent } from '../dialogs/delete-emergency-contact-dialog/delete-emergency-contact-dialog.component';
import { NoopScrollStrategy } from '@angular/cdk/overlay';


@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss']
})
export class LayoutComponent extends MobileApp implements OnInit, OnDestroy {
  @ViewChild('emergencyContactsPopup', { read: ElementRef }) emergencyContactsPopup: ElementRef;
  userUid: string = this.localStorage.retrieve('loggedUser')['user_booking_uid'];
  userId: any;
  changePassModel: ChangePasswordModel = new ChangePasswordModel();
  editProfileModel: EditProfileModel = new EditProfileModel();
  emergencyContacts: EmergencyContact[] = [];
  changePassSubmited: boolean = false;
  editProfileSubmited: boolean = false;
  emergencyContactsSubmitted: boolean = false;
  settingsShown: boolean = false
  imageFile: string = 'assets/images/person.png';
  fileToUpload: File;
  userName: string = '';
  title: string;
  userRole: any;
  visitId: any;
  chatRoom: any;
  chatTitle: any;
  hasAvatar: boolean = false;
  private subscriptions: Subscription[] = [];

  navBarElement: HTMLDivElement | null;
  userCredits: number = 0;
  userActiveSubscriptionPlan!: ISubscriptionPlan;

  constructor(
    private apiService: ApiService,
    private userService: UserService,
    private localStorage: LocalStorageService,
    private router: Router,
    private titleService: TitleService,
    private editProfileService: EditProfileService,
    private socketIO: SocketIoService,
    private billingService: BillingService,
    private chatService: ChatService,
    private dialog: MatDialog
  ) {
    super();
    this.subscriptions.push(this.titleService.title.subscribe((t) => {
      this.title = t;
    }));

    this.subscriptions.push(this.chatService.callStatus$.subscribe((status: any) => {
      if (status == VideoCallStatus.ended) {
        this.billingService.updateCredits(this.userUid);
      }
    }));

    this.userId = apiService.getLoggedUserID();
    this.visitId = this.localStorage.retrieve('loggedUser').visit_id;
    this.chatRoom = 'patient_' + this.userId;
    this.chatTitle = 'Chat room ' + this.userId;
    this.getEmergencyContacts();
  }

  ngOnInit(): void {
    this.subscriptions.push(this.editProfileService.openEmergencyContacts.subscribe(() => {
      this.settingsShown = true;
      this.openPopup(this.emergencyContactsPopup.nativeElement);
    }));
    let loggedUser = this.localStorage.retrieve('loggedUser');
    if (loggedUser.imageUrl) {
      let assetsFolder = environment.imageDir;
      this.imageFile = assetsFolder + loggedUser.imageUrl;
      this.hasAvatar = true;
    }
    this.userName = loggedUser.first_name + ' ' + loggedUser.last_name;
    this.editProfileModel.first_name = loggedUser.first_name || '';
    this.editProfileModel.last_name = loggedUser.last_name || '';
    this.editProfileModel.birth_date = loggedUser.birth_date || '';
    this.editProfileModel.email = loggedUser.email || '';
    this.editProfileModel.phoneNumber = loggedUser.phone_number || '';
    this.editProfileModel.gender = loggedUser.gender || "male";
    this.editProfileModel.weight = loggedUser.weight || '';
    this.editProfileModel.height = loggedUser.height || '';
    this.socketIO.initializeConnection();
    this.getUserActiveSubscriptionPlan();

    this.billingService.updateCredits(this.userUid);

    this.billingService.currentUserCredits$.subscribe((response: any) => {
      this.userCredits = response.credits;
    });
  }

  openProfileSettings() {
    this.settingsShown = !this.settingsShown;

    if (this.settingsShown) {
      this.getEmergencyContacts();
    }
  }

  onProfileImgSelect(files: FileList): void {
    let file = files.item(0);
    this.fileToUpload = file as any;
    const reader = new FileReader();
    reader.readAsDataURL(file as any);
    reader.onload = (_event) => {
      if (typeof reader.result === 'string') {
        this.imageFile = reader.result;
      }
    }
  }

  getEmergencyContacts(): void {
    this.apiService.listEmergencyContacts(this.visitId).subscribe({
      next: (response: any) => {
        if(response) {
          this.emergencyContacts = response.data;
        }
      },
      error: (err: any) => { console.error(err)},
      complete: () => {}
    });
  }

  openAddEmergencyContactDialog(outerPopup: any, emergencyContact: EmergencyContact | undefined = undefined): void {
    this.closePopup(outerPopup);
    let dialogRef = this.dialog.open(AddEmergencyContactDialogComponent, {
      data: {
        visitId: this.visitId,
        isEdit: emergencyContact != undefined,
        contactName: emergencyContact?.PatientContactName,
        contactPhone: emergencyContact?.PatientContactPhone,
        contactId: emergencyContact?.PatientContactID
      },
      autoFocus: false,
      restoreFocus: false,
      scrollStrategy: new NoopScrollStrategy()
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      this.openPopup(outerPopup);
      if (result) {
        let idx = this.emergencyContacts.findIndex((contact: EmergencyContact) => contact.PatientContactID == result.PatientContactID);
        if (idx != -1) {
          this.emergencyContacts[idx] = result;
        } else {
          this.emergencyContacts.push(result);
        }
      }
    });
  }

  openDeleteEmergencyContactDialog(outerPopup: any, contact: EmergencyContact) {
    this.closePopup(outerPopup);

    let dialogRef = this.dialog.open(DeleteEmergencyContactDialogComponent, {
      data: {
        visitId: this.visitId,
        contactId: contact.PatientContactID,
        contactName: contact.PatientContactName,
        contactPhone: contact.PatientContactPhone
      }
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      this.openPopup(outerPopup);
      if (result) {
        let idx = this.emergencyContacts.findIndex((contact: EmergencyContact) => contact.PatientContactID == result);
        if (idx != -1) {
          this.emergencyContacts.splice(idx, 1);
        }
      }
    });
  }

  openPopup(popup: any): void {
    popup.showModal();
  }

  closePopup(popup: any): void {
    popup.close();
  }

  onEmergencyContactSubmit(popup: any): void {
    this.emergencyContactsSubmitted = true;
    let name = '';
    let phone = '';
    this.apiService.addEmergencyContact(this.visitId, name, phone).subscribe({
      next: (response: any) => {
        if (response) {
          popup.close();
        }
      },
      error: (err: any) => console.error(err),
      complete: () => {}
    });
  }

  onEditProfileSubmit(form: NgForm, popup: any): void {
    this.editProfileSubmited = true;
    if (form.valid) {
      const formData: FormData = new FormData();
      formData.append("id", this.userId);
      formData.append("first_name", this.editProfileModel.first_name);
      formData.append("last_name", this.editProfileModel.last_name);
      formData.append("birth_date", this.editProfileModel.birth_date);
      formData.append("email", this.editProfileModel.email);
      formData.append("phone_number", this.editProfileModel.phoneNumber);
      formData.append("gender", this.editProfileModel.gender);
      formData.append("weight", this.editProfileModel.weight);
      formData.append("height", this.editProfileModel.height);
      if (this.fileToUpload) {
        formData.append("file", this.fileToUpload, this.fileToUpload.name);
      }

      this.apiService.updateUser(formData)
        .subscribe(
          data => {
            if (data.success == true) {
              data.data.userToken = this.localStorage.retrieve('loggedUser').userToken;
              this.localStorage.store('loggedUser', data.data);
              this.closePopup(popup);
              this.editProfileSubmited = false;
              // form.reset();
            }
          },
          error => {
            console.error(error)
          });
    }
  }

  onChangePassSubmit(form: NgForm, popup: any): void {
    this.changePassSubmited = true;
    if (form.valid) {
      let old_password = this.changePassModel.oldPassword;
      let new_password = this.changePassModel.newPassword;
      let password_repeat = this.changePassModel.repeatPassword;
      let requestData = { old_password, new_password, password_repeat };
      this.userService.changeUserPassword(requestData).subscribe({
        next: (response: any) => {
          if (response.success == true) {
            this.closePopup(popup);
            this.changePassSubmited = false;
            form.reset();
          }
        },
        error: (err: any) => console.error(err),
        complete: () => { }
      });
    }
  }

  logOut(): void {
    let logoutData = {
      'session': {
        'loggedUser': null,
        'token': ''
      }
    };
    logoutData.session.loggedUser = this.localStorage.retrieve('loggedUser');
    logoutData.session.token = this.localStorage.retrieve('token');

    this.apiService.doLogoutAut(logoutData).subscribe(
      (data) => {
        if (data?.success !== true) {
          alert(data?.data?.message);
        } else {
          if (this.socketIO.isConnected()) {
            this.socketIO.closeConnection();
          }
          this.localStorage.clear();
          this.router.navigate(['/login']);
        }
      },
      error => {
        alert('The session has NOT been deleted successfully!' + error?.statusText);
      }
    );
  }

  public checkRole(roles: any) {
    return this.localStorage.retrieve('loggeduser').roles.some((x: any) => roles.includes(x));
  }

  gotToDashboard() {
    if (this.checkRole(['patient'])) {
      this.router.navigateByUrl('/dashboard');
    }
  }

  gotToHome() {
    this.router.navigateByUrl('/home');
  }

  toggleNavBar(e, t: MatMenuTrigger) {
    this.navBarElement = e.currentTarget
    if (t.openMenu) {
      let sub = t.menuClosed.subscribe(
        () => {
          this.navBarElement?.classList.toggle('change')
          this.navBarElement = null
          sub.unsubscribe()
        })
    }

    this.navBarElement?.classList.toggle('change')
  }

  showInitials() {
    let loggedUser = this.localStorage.retrieve('loggedUser');
    if (!loggedUser || !loggedUser.first_name || !loggedUser.last_name) {
      return '';
    }

    let firstInitial = loggedUser.first_name.charAt(0).toUpperCase();
    let lastInitial = loggedUser.last_name.charAt(0).toUpperCase();

    return `${firstInitial}${lastInitial}`;
  }


  redirectToBuyCredits() {
    this.router.navigate(['billing/buy-credits']);
  }

  redirectToPayments() {
    this.router.navigate(['billing/payments']);
  }

  redirectToRequestMedicalObservation() {
    this.router.navigate(['billing/request-medical-observation']);
  }

  getUserActiveSubscriptionPlan() {
    this.billingService.getUserActiveSubscription(this.userUid).subscribe({
      next: (response: any) => {
        if (response) {
          this.userActiveSubscriptionPlan = response;
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.map(s => s.unsubscribe);
  }

}
